import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Edit, History, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import toast from 'react-hot-toast';

const COLOR_CONFIG = {
  weiss: {
    label: 'Weiß',
    bg: 'bg-gray-50',
    border: 'border-gray-300',
    dot: 'bg-white border-2 border-gray-300',
    badge: 'bg-gray-100 text-gray-800',
  },
  schwarz: {
    label: 'Schwarz',
    bg: 'bg-gray-900',
    border: 'border-gray-700',
    dot: 'bg-gray-900 border-2 border-gray-600',
    badge: 'bg-gray-800 text-gray-100',
  },
  rosa: {
    label: 'Rosa',
    bg: 'bg-pink-50',
    border: 'border-pink-300',
    dot: 'bg-pink-300 border-2 border-pink-400',
    badge: 'bg-pink-100 text-pink-800',
  },
  mint: {
    label: 'Mint',
    bg: 'bg-teal-50',
    border: 'border-teal-300',
    dot: 'bg-teal-300 border-2 border-teal-400',
    badge: 'bg-teal-100 text-teal-800',
  },
};

export function InventoryPage() {
  const [colorPools, setColorPools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editForm, setEditForm] = useState({ totalUnits: 0, reason: '' });
  const [history, setHistory] = useState([]);
  const [schedules, setSchedules] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ effectiveDate: '', totalUnits: 0, note: '' });

  useEffect(() => {
    loadColorInventory();
  }, []);

  const loadColorInventory = async () => {
    try {
      setLoading(true);
      const response = await api.getColorInventory();
      setColorPools(response.colorPools || []);

      // Load schedules for all colors
      const schedulesMap = {};
      for (const pool of response.colorPools || []) {
        try {
          const scheduleData = await api.getColorInventorySchedule(pool.color);
          schedulesMap[pool.color] = scheduleData.schedules || [];
        } catch (error) {
          schedulesMap[pool.color] = [];
        }
      }
      setSchedules(schedulesMap);
    } catch (error) {
      console.error('Error loading color inventory:', error);
      toast.error('Fehler beim Laden des Inventars');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (pool) => {
    setSelectedItem(pool);
    setEditForm({ totalUnits: pool.total_units, reason: '' });
    setShowEditModal(true);
  };

  const handleUpdateInventory = async () => {
    if (!selectedItem) return;

    if (editForm.totalUnits < 0) {
      toast.error('Die Anzahl muss mindestens 0 sein');
      return;
    }
    if (!editForm.reason || editForm.reason.trim() === '') {
      toast.error('Bitte geben Sie einen Grund für die Änderung an');
      return;
    }

    try {
      await api.updateColorInventory(selectedItem.color, editForm.totalUnits, editForm.reason);
      toast.success('Inventar erfolgreich aktualisiert');
      await loadColorInventory();
      setShowEditModal(false);
      setSelectedItem(null);
      setEditForm({ totalUnits: 0, reason: '' });
    } catch (error) {
      console.error('Error updating color inventory:', error);
      toast.error('Fehler beim Aktualisieren des Inventars');
    }
  };

  const handleHistoryClick = async (pool) => {
    setSelectedItem(pool);
    setShowHistoryModal(true);
    setLoadingHistory(true);
    try {
      const response = await api.getColorInventoryHistory(pool.color);
      setHistory(response.history || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Historie');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleScheduleClick = async (pool) => {
    setSelectedItem(pool);
    setShowScheduleModal(true);
    setLoadingSchedules(true);
    try {
      const response = await api.getColorInventorySchedule(pool.color);
      setSchedules(prev => ({ ...prev, [pool.color]: response.schedules || [] }));
    } catch (error) {
      toast.error('Fehler beim Laden der Produktionspläne');
    } finally {
      setLoadingSchedules(false);
    }
  };

  const handleAddSchedule = async () => {
    if (!selectedItem) return;
    if (!scheduleForm.effectiveDate) {
      toast.error('Bitte geben Sie ein Datum an');
      return;
    }
    if (scheduleForm.totalUnits < 0) {
      toast.error('Die Anzahl muss mindestens 0 sein');
      return;
    }
    try {
      await api.addColorInventorySchedule(
        selectedItem.color,
        scheduleForm.effectiveDate,
        scheduleForm.totalUnits,
        scheduleForm.note
      );
      toast.success('Produktionsplan erfolgreich hinzugefügt');
      const response = await api.getColorInventorySchedule(selectedItem.color);
      setSchedules(prev => ({ ...prev, [selectedItem.color]: response.schedules || [] }));
      setScheduleForm({ effectiveDate: '', totalUnits: 0, note: '' });
    } catch (error) {
      toast.error('Fehler beim Hinzufügen des Produktionsplans');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!confirm('Möchten Sie diesen Produktionsplan wirklich löschen?')) return;
    try {
      await api.deleteColorInventorySchedule(scheduleId);
      toast.success('Produktionsplan erfolgreich gelöscht');
      const response = await api.getColorInventorySchedule(selectedItem.color);
      setSchedules(prev => ({ ...prev, [selectedItem.color]: response.schedules || [] }));
    } catch (error) {
      toast.error('Fehler beim Löschen des Produktionsplans');
    }
  };

  const getNextSchedule = (color) => {
    const colorSchedules = schedules[color] || [];
    const today = new Date().toISOString().split('T')[0];
    const future = colorSchedules.filter(s => s.effective_date > today);
    if (future.length === 0) return null;
    return future[0];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lade Inventar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Fotobox-Inventar</h1>
        <p className="mt-1 text-sm text-gray-500">
          Physische Fotoboxen nach Farbe verwalten
        </p>
      </div>

      {/* Info Box */}
      <Card className="p-4 bg-blue-50 border border-blue-200">
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">Farb-basiertes Inventar</p>
          <p className="text-blue-700">
            Alle Fotoboxen sind physisch gleich. Das Inventar wird pro Farbe verwaltet — unabhängig davon,
            welches Paket (Eco, Premium, Business) gebucht wird. Eine gebuchte Weiß-Box zählt für alle Paket-Varianten.
          </p>
        </div>
      </Card>

      {/* Color Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {colorPools.map((pool) => {
          const config = COLOR_CONFIG[pool.color] || COLOR_CONFIG.weiss;
          const nextSchedule = getNextSchedule(pool.color);
          const isDark = pool.color === 'schwarz';

          return (
            <Card key={pool.color} className={`p-6 border-2 ${config.border}`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full ${config.dot} shadow-sm`} />
                <h2 className="text-xl font-bold text-gray-900">
                  {pool.display_name}
                </h2>
              </div>

              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-gray-900">
                    {pool.total_units}
                  </span>
                  <span className="text-sm text-gray-500">
                    {pool.total_units === 1 ? 'Fotobox' : 'Fotoboxen'}
                  </span>
                </div>
                {nextSchedule && (
                  <p className="text-xs mt-1 text-gray-500">
                    → {nextSchedule.total_units} ab {format(new Date(nextSchedule.effective_date), 'dd.MM.yyyy', { locale: de })}
                    {nextSchedule.note && ` (${nextSchedule.note})`}
                  </p>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleEditClick(pool)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors bg-blue-100 hover:bg-blue-200 text-blue-800"
                >
                  <Edit className="w-3.5 h-3.5" />
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleScheduleClick(pool)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors bg-green-100 hover:bg-green-200 text-green-800"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Vorplanung
                </button>
                <button
                  onClick={() => handleHistoryClick(pool)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  <History className="w-3.5 h-3.5" />
                  Historie
                </button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Bestand bearbeiten — {selectedItem.display_name}
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Aktueller Bestand:</strong> {selectedItem.total_units} {selectedItem.total_units === 1 ? 'Fotobox' : 'Fotoboxen'}
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Neue Anzahl
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.totalUnits}
                    onChange={(e) => setEditForm({ ...editForm, totalUnits: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grund für Änderung
                  </label>
                  <textarea
                    value={editForm.reason}
                    onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                    placeholder="z.B. Neue Einheit gekauft, Defekte Einheit ausgeschieden..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button
                  variant="secondary"
                  onClick={() => { setShowEditModal(false); setSelectedItem(null); }}
                >
                  Abbrechen
                </Button>
                <Button onClick={handleUpdateInventory}>
                  Speichern
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Bestandshistorie</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedItem.display_name}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {loadingHistory ? (
                <div className="text-center py-8 text-gray-500">Lade Historie...</div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-gray-500">Keine Änderungen aufgezeichnet</div>
              ) : (
                <div className="space-y-3">
                  {history.map((entry) => (
                    <div key={entry.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {entry.old_quantity} → {entry.new_quantity}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            entry.new_quantity > entry.old_quantity
                              ? 'bg-green-100 text-green-800'
                              : entry.new_quantity < entry.old_quantity
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {entry.new_quantity > entry.old_quantity
                              ? `+${entry.new_quantity - entry.old_quantity}`
                              : entry.new_quantity - entry.old_quantity}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {format(new Date(entry.changed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-1">{entry.reason}</p>
                      <p className="text-xs text-gray-500">von {entry.changed_by}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => { setShowHistoryModal(false); setSelectedItem(null); setHistory([]); }}
                className="w-full"
              >
                Schließen
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Vorplanung</h3>
              <p className="text-sm text-gray-600 mt-1">{selectedItem.display_name}</p>
              <p className="text-xs text-gray-500 mt-1">
                Definieren Sie zukünftige Inventar-Stände — z.B. wenn eine neue Box geliefert wird
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {/* Add New Schedule */}
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Neuen Stichtag hinzufügen</h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Datum (ab wann gilt die neue Anzahl)
                    </label>
                    <input
                      type="date"
                      value={scheduleForm.effectiveDate}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, effectiveDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Anzahl Fotoboxen ({selectedItem.display_name})
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={scheduleForm.totalUnits}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, totalUnits: parseInt(e.target.value) || 0 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notiz (optional)
                    </label>
                    <input
                      type="text"
                      placeholder="z.B. Neue Box aus Produktion erhalten"
                      value={scheduleForm.note}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, note: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <Button onClick={handleAddSchedule} className="w-full">
                    Stichtag hinzufügen
                  </Button>
                </div>
              </div>

              {/* Existing Schedules */}
              {loadingSchedules ? (
                <div className="text-center py-8 text-gray-500">Lade Produktionspläne...</div>
              ) : (() => {
                const colorSchedules = schedules[selectedItem.color] || [];
                return colorSchedules.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Noch keine Vorplanung definiert
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Geplante Inventar-Stände</h4>
                    {colorSchedules.map((schedule) => {
                      const today = new Date().toISOString().split('T')[0];
                      const isPast = schedule.effective_date < today;
                      const isCurrent = !isPast && colorSchedules.filter(s => s.effective_date <= today).length > 0
                        && colorSchedules.filter(s => s.effective_date <= today).at(-1)?.id === schedule.id;

                      return (
                        <div
                          key={schedule.id}
                          className={`border rounded-lg p-4 ${
                            isPast ? 'border-gray-200 bg-gray-50' :
                            isCurrent ? 'border-green-300 bg-green-50' :
                            'border-blue-200 bg-blue-50'
                          }`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium text-gray-900">
                                  {format(new Date(schedule.effective_date), 'dd. MMMM yyyy', { locale: de })}
                                </span>
                                {isPast && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">Vergangen</span>
                                )}
                                {isCurrent && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-200 text-green-800">Aktuell aktiv</span>
                                )}
                                {!isPast && !isCurrent && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-200 text-blue-800">Zukünftig</span>
                                )}
                              </div>
                              <p className="text-lg font-bold text-gray-900 mb-1">
                                {schedule.total_units} {schedule.total_units === 1 ? 'Fotobox' : 'Fotoboxen'}
                              </p>
                              {schedule.note && (
                                <p className="text-sm text-gray-600">{schedule.note}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-2">
                                Erstellt von {schedule.created_by} am {format(new Date(schedule.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                              </p>
                            </div>
                            <button
                              onClick={() => handleDeleteSchedule(schedule.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium ml-4"
                            >
                              Löschen
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
            <div className="p-6 border-t border-gray-200">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowScheduleModal(false);
                  setSelectedItem(null);
                  setScheduleForm({ effectiveDate: '', totalUnits: 0, note: '' });
                }}
                className="w-full"
              >
                Schließen
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
